import { collection, addDoc, doc, setDoc, Timestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut, updateCurrentUser, getIdToken } from 'firebase/auth';
import { db, auth } from '../../../../../firebase/config';

export type PSOFormData = {
    name: string;
    contact: string;
    email: string;
    designation: string;
    password?: string;
};

// Check if designation is an incharge type
const isInchargeDesignation = (designation: string): boolean => {
    const inchargeDesignations = [
        'Incharge CCTV',
        'Incharge BAMS',
        'Incharge Guarding',
        'Incharge MIS'
    ];
    return inchargeDesignations.includes(designation.trim());
};

export async function submitPSO(bankId: string, formData: PSOFormData): Promise<void> {
    if (!bankId) {
        throw new Error('Bank ID is required');
    }

    // Normalize email: trim and convert to lowercase for consistent comparison
    const email = formData.email.trim().toLowerCase();
    const designation = formData.designation.trim();
    const isIncharge = isInchargeDesignation(designation);

    // If it's an incharge, create Firebase Auth user first
    let authUid: string | undefined;
    if (isIncharge) {
        if (!formData.password || formData.password.trim().length < 6) {
            throw new Error('Password is required for incharge and must be at least 6 characters');
        }

        // Save current user session to restore after creating new user
        const currentUser = auth.currentUser;

        if (!currentUser || !currentUser.uid) {
            throw new Error('No user is currently logged in. Please log in as admin to create staff members.');
        }

        const currentUserUid = currentUser.uid;
        // Save the admin's User object to restore later
        const adminUser = currentUser;

        // Set a flag to prevent auth state listener from processing the temporary session switch
        sessionStorage.setItem('creatingUser', currentUserUid);
        console.log('Set flag to prevent auth state change processing. Current admin:', currentUserUid);

        try {
            console.log('Attempting to create Firebase Auth user for email:', email);
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                email,
                formData.password.trim()
            );
            authUid = userCredential.user.uid;
            console.log('Firebase Auth user created successfully with UID:', authUid);

            // Immediately restore the admin's session using updateCurrentUser
            // The flag in sessionStorage will prevent any UI updates during this operation
            try {
                // Reload the admin user to ensure the User object is fresh
                await adminUser.reload();
                
                // Use updateCurrentUser to restore the admin session immediately
                // This should restore the session before the auth state listener can process the new user
                await updateCurrentUser(auth, adminUser);
                console.log('Admin session restored successfully');
                
                // Double-check that the session was restored
                const currentAuthUser = auth.currentUser;
                if (currentAuthUser && currentAuthUser.uid === currentUserUid) {
                    console.log('Session restore verified - admin is still logged in');
                } else {
                    console.warn('Session restore may have failed. Current user:', currentAuthUser?.uid, 'Expected:', currentUserUid);
                    // The flag will prevent UI updates, so even if restore didn't work perfectly,
                    // the UI will remain unchanged
                }
            } catch (restoreError: any) {
                console.error('Failed to restore admin session:', restoreError);
                // Even if restore fails, the flag will prevent UI updates
                // The admin's UI state will remain intact
                console.warn('Session restore failed, but flag will prevent UI changes. Admin should refresh if issues occur.');
            }

        } catch (error: any) {
            // Clear the flag if creation fails
            sessionStorage.removeItem('creatingUser');
            // Handle Firebase Auth errors
            const errorCode = error?.code;
            const errorMessage = error?.message || 'Unknown error occurred';
            
            console.error('Firebase Auth error:', errorCode, errorMessage);
            
            if (errorCode === 'auth/email-already-in-use') {
                // Email exists in Firebase Auth but not in Firestore - this is a data inconsistency
                // This can happen if a previous attempt created the Auth user but failed to create the Firestore document
                console.warn('Email exists in Firebase Auth but not in Firestore. This may indicate a previous incomplete registration.');
                throw new Error('This email is already registered in the authentication system. If you believe this is an error, please contact an administrator or try using a different email address.');
            } else if (errorCode === 'auth/invalid-email') {
                throw new Error('Invalid email address. Please check the email format.');
            } else if (errorCode === 'auth/weak-password') {
                throw new Error('Password is too weak. Please use a stronger password (at least 6 characters).');
            } else if (errorCode === 'auth/operation-not-allowed') {
                throw new Error('Email/password accounts are not enabled. Please contact an administrator.');
            } else {
                throw new Error(`Failed to create user account: ${errorMessage}`);
            }
        }
    }

    // For incharge designations, replace spaces with underscores when saving to DB
    const designationForDB = isIncharge 
        ? designation.replace(/\s+/g, '_') 
        : designation;

    const psoData: any = {
        name: formData.name.trim(),
        contact: formData.contact.trim(),
        email: email,
        designation: designationForDB,
        isActive: true,
        createdAt: Timestamp.now(),
    };

    // If it's an incharge with authUid, use setDoc with UID as document ID
    if (isIncharge && authUid) {
        psoData.authUid = authUid;
        const psoRef = doc(db, 'banks', bankId, 'pos', authUid);
        await setDoc(psoRef, psoData);
        
        // Clear the flag after successfully creating the Firestore document
        // The admin session should already be restored via updateCurrentUser
        sessionStorage.removeItem('creatingUser');
        console.log('User creation completed. Admin session should be restored.');
    } else {
        // For non-incharge, use addDoc (auto-generated ID)
        await addDoc(collection(db, 'banks', bankId, 'pos'), psoData);
    }
}

